'use client'

import { clsx } from 'clsx'
import { type FC, useRef } from 'react'

import { Link } from 'base-ui'

import { type AttributeGroupData } from '../../../product-page-context/AttributeGroup'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPageAttributeHead } from '../ProductPageAttributeHead'
import { ProductPageAttributeSelector } from '../ProductPageAttributeSelector'
import { useAttributeSelectionCollapsible } from '../useAttributeSelectionCollapsible'
import { useScrollElementIntoView } from '../useScrollElementIntoView'

interface ProductPageTextTypeAttributeGroupProps {
  attributeGroup: AttributeGroupData
  className?: string
  isSingleGroup: boolean
}

export const ProductPageTextTypeAttributeGroup: FC<
  ProductPageTextTypeAttributeGroupProps
> = ({ attributeGroup, className, isSingleGroup }) => {
  const scrollableElementRef = useRef(null)

  const handleScrollToElement = useScrollElementIntoView(scrollableElementRef)

  const {
    metaAttributeData,
    selectionEntries,
    shouldDisplayOnlySelectedValue,
  } = attributeGroup
  const selectedEntry = selectionEntries.find((it) => it.isSelected)
  const collapsibleData = useAttributeSelectionCollapsible({
    isSingleGroup,
    metaAttributeData,
    selectionEntries,
  })
  const { attributeSelectionControls } = useProductPageContext()
  const attributeNotSelectedError =
    attributeSelectionControls.attributeNotSelectedErrorControls.getShouldShowAttributeMustBeSelectedErrorForGroup(
      attributeGroup,
    )

  return (
    <div className={clsx(className, 'flex flex-col')}>
      <ProductPageAttributeHead
        metaAttribute={metaAttributeData}
        selectedEntry={selectedEntry}
      />
      {!shouldDisplayOnlySelectedValue && (
        <>
          <div
            className="flex flex-row gap-2 overflow-x-auto pb-2.5 scrollbar-hide tablet:grid desktop:grid-cols-2"
            ref={scrollableElementRef}
          >
            {selectionEntries.map((entry, index) => {
              return (
                <ProductPageAttributeSelector
                  className={clsx(
                    collapsibleData.maxVisibleIndex <= index && 'tablet:hidden',
                  )}
                  error={attributeNotSelectedError}
                  key={`${entry.attribute.value ?? ''}${
                    entry.allCandidateVariants[0]?.upid
                  }`}
                  label={entry.attribute.value}
                  onScrollIntoView={handleScrollToElement}
                  selectionEntry={entry}
                />
              )
            })}
          </div>
          {collapsibleData.showLabel ? (
            <Link
              className="mt-3 hidden w-full flex-row items-center justify-center tablet:flex"
              color="brand"
              onClick={collapsibleData.handleLabelClick}
              slots={{ root: 'button' }}
              type="button"
            >
              {collapsibleData.labelContent}
            </Link>
          ) : null}
        </>
      )}
    </div>
  )
}
